import styled from 'styled-components'
import { Box, Chip, Select } from '@mui/material'
import { breakpoints, colors, fontFamily, fontWeight } from 'theme'
import { SyncLifecycleState } from 'api/models'

type StyledStatuses =
  | SyncLifecycleState.COMPLETED
  | SyncLifecycleState.IMPORTING
  | SyncLifecycleState.PUBLISHING
  | SyncLifecycleState.INELIGIBLE
  | SyncLifecycleState.PENDING
  | SyncLifecycleState.ACTIVE


export const getStatusColor = (status: SyncLifecycleState): string =>
  statusColorMap[status as StyledStatuses] ?? '#9E9E9E'

export const getStatusBackground = (status: SyncLifecycleState): string =>
  statusBackgroundMap[status as StyledStatuses] ?? '#F5F5F5'


// Status color mapping (used in StatusDot and StatusText)
export const statusColorMap: Pick<Record<SyncLifecycleState, string>, StyledStatuses> = {
  completed: '#32965D',
  importing: '#F4A100',
  publishing: '#007AFF',
  ineligible: '#C13030',
  pending: '#9E9E9E',
  active: '#4A90E2',
}

// Status background mapping (used in StatusChip)
const statusBackgroundMap: Pick<Record<SyncLifecycleState, string>, StyledStatuses> = {
  completed: '#E0F3EA',
  importing: '#FFF3CD',
  publishing: '#E3F2FD',
  ineligible: '#FDECEA',
  pending: '#F5F5F5',
  active: '#E3F2FD',
}

export const ModalWrapper = styled(Box)({
  width: '100%',
  height: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
})

export const ModalHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  borderBottom: `1px solid ${colors.gray[300]}`,
})

export const ModalContent = styled.div({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
})

export const ModalTitle = styled.h3({
  fontFamily: fontFamily.inter,
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '140%',
  letterSpacing: 0,
  verticalAlign: 'bottom',
  color: '#292929',
})

export const EmptyStateWrapper = styled.div({
  textAlign: 'center',
})

export const EmptyStateText = styled.p({
  color: '#777',
  fontFamily: fontFamily.inter,
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '150%',
})

export const TablePaginationWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden',
})

export const ScrollableTableContainer = styled('div')({
  flexGrow: 1,
  overflowY: 'auto',
  minHeight: 0,
  maxHeight: '400px',
})

export const PaginationWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '16px',
  gap: '16px',
  fontSize: '13px',
  [`@media ${breakpoints.xs_max}`]: {
    justifyContent: 'center',
  },
})

export const CurrentPageInfo = styled.div({
  fontFamily: fontFamily.inter,
  fontSize: '13px',
  textAlign: 'left',
  [`@media ${breakpoints.xs_max}`]: {
    display: 'none',
  },
})

export const RowsPerPageWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  [`@media ${breakpoints.xs_max}`]: {
    display: 'none',
  },
})

export const PaginationText = styled.span({
  fontFamily: fontFamily.inter,
  fontSize: '13px',
  marginRight: 0,
})

export const PaginationSelect = styled(Select)({
  '.MuiInputBase-input': {
    fontSize: '13px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
})

export const TimestampText = styled.span({
  fontFamily: fontFamily.inter,
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '0%',
  verticalAlign: 'bottom',
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  textDecorationOffset: '0%',
  textDecorationThickness: '0%',
  color: '#1B51A4',
  cursor: 'pointer',
})

export const StatusWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  textAlign: 'right',
  verticalAlign: 'bottom',
})

export const StatusText = styled.span<{ status: SyncLifecycleState }>((props) => ({
  fontFamily: fontFamily.inter,
  fontWeight: 500,
  fontSize: '10px',
  lineHeight: '150%',
  letterSpacing: 0,
  textAlign: 'right',
  verticalAlign: 'bottom',
  textTransform: 'capitalize',
  color: getStatusColor(props.status),
}))

export const StatusDot = styled.div<{ status: SyncLifecycleState }>((props) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: getStatusColor(props.status),
  flexShrink: 0,
}))

export const DrawerHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px',
  borderBottom: 'solid 1px #ccc',

  '& h2': {
    fontFamily: fontFamily.inter,
    fontSize: '20px',
    fontWeight: fontWeight.semibold,
    margin: 0,
  },
})

export const DrawerContent = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column' as const,
  padding: 15,
}))

export const DrawerRow = styled.div<{ full?: boolean }>(({ full }) => ({
  display: 'flex',
  padding: '8px 0',
  flexDirection: 'column' as const,
  fontFamily: fontFamily.inter,
  fontSize: 13,
  color: '#292929',
  width: full ? '100%' : '496px',
  fontWeight: fontWeight.medium,
  [`@media ${breakpoints.xs_max}`]: {
    width: '300px',
  },
}))

export const DrawerRowPair = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
})

export const DrawerLabel = styled.div({
  fontFamily: fontFamily.inter,
  fontSize: '11px',
  marginBottom: 10,
  color: '#777777',
  fontWeight: fontWeight.medium,
})

export const DrawerValue = styled.div<{ horizontal?: boolean }>(({ horizontal }) => ({
  textAlign: 'left',
  color: '#292929',
  display: 'flex',
  flexDirection: horizontal ? 'row' : 'column',
  alignItems: horizontal ? 'center' : 'flex-start',
  justifyContent: 'flex-start',
  gap: horizontal ? '8px' : '0',
  marginBottom: 10,
  whiteSpace: 'nowrap',
}))

export const StatusChip = styled(Chip)<{ status: SyncLifecycleState }>((props) => ({
  '&&': {
    width: 'fit-content',
    height: 24,
    borderRadius: 5,
    padding: '4px',
    gap: '5px',
    fontFamily: fontFamily.inter,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '16px',
    textTransform: 'capitalize',
    backgroundColor: getStatusBackground(props.status),
    color: getStatusColor(props.status),
  }
}))

export const PlatformStatusText = styled.span<{ success?: boolean }>((props) => ({
  fontWeight: 500,
  fontSize: '13px',
  color: props.success ? '#32965D' : '#C13030',
  fontFamily: fontFamily.inter,
}))

export const PlatformMetaText = styled.span({
  fontSize: '9px',
  color: '##292929',
  fontFamily: fontFamily.inter,
  lineHeight: '1.5',
})